import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Users,
  Building,
  Calendar,
  AlertTriangle,
  TrendingUp,
  Clock,
  FileText,
  Plus,
  Home,
  Activity,
  RefreshCw,
  Settings,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentShiftLogs, setRecentShiftLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hasSampleData, setHasSampleData] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);

  const fetchSampleStatus = async () => {
    try {
      const res = await axios.get(`${API}/sample-data/status`);
      setHasSampleData(res.data.has_sample_data);
    } catch (e) { console.warn('sample-data status fetch failed:', e?.response?.status); }
  };

  const handleLoadSample = async () => {
    setSampleLoading(true);
    try {
      await axios.post(`${API}/sample-data/load`);
      toast.success('Sample data loaded — explore the dashboard!');
      await fetchSampleStatus();
      fetchDashboardStats(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load sample data');
    } finally { setSampleLoading(false); }
  };

  const handleClearSample = async () => {
    if (!window.confirm('Remove all sample data? Your real data (if any) is not affected.')) return;
    setSampleLoading(true);
    try {
      const res = await axios.post(`${API}/sample-data/clear`);
      toast.success(`Sample data cleared (${res.data.residents} residents, ${res.data.rooms} rooms)`);
      setHasSampleData(false);
      fetchDashboardStats(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear sample data');
    } finally { setSampleLoading(false); }
  };

  useEffect(() => {
    // Initial load with slight delay to ensure backend is ready
    const initialLoad = setTimeout(() => {
      fetchDashboardStats(0);
      fetchRecentShiftLogs();
      fetchSampleStatus();
    }, 500);
    
    // Set up intervals for real-time updates
    const statsInterval = setInterval(() => fetchDashboardStats(0), 300000); // Every 5 minutes
    const logsInterval = setInterval(fetchRecentShiftLogs, 600000); // Every 10 minutes
    
    // Update time every minute
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    // Handle page visibility changes (when user comes back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('📱 Page became visible, refreshing data');
        fetchDashboardStats(0);
        fetchRecentShiftLogs();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(initialLoad);
      clearInterval(statsInterval);
      clearInterval(logsInterval);
      clearInterval(timeInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchDashboardStats = async (retryCount = 0) => {
    const maxRetries = 3;
    
    try {
      console.log(`🔍 Fetching dashboard stats (attempt ${retryCount + 1}/${maxRetries + 1}):`, `${API}/dashboard/stats`);
      
      // Check if user is authenticated before making API call
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found');
        toast.error('Please log in to view dashboard data');
        setStats({ _error: true, _auth_required: true });
        return;
      }

      // Aggressive cache-busting with multiple parameters
      const timestamp = new Date().getTime();
      const random = Math.random().toString(36).substring(7);
      const response = await axios.get(`${API}/dashboard/stats`, {
        params: {
          _t: timestamp,
          _r: random,
          _v: '2.0', // Version parameter
          cache_bust: true
        },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'X-Requested-With': 'XMLHttpRequest',
          'Authorization': `Bearer ${token}` // Ensure token is sent
        },
        timeout: 15000 // 15 second timeout
      });
      
      console.log('📊 Dashboard stats response received:', response.data);
      console.log('📊 Response status:', response.status);
      
      // Validate response structure
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response data structure');
      }
      
      // Check for required fields
      const requiredFields = ['total_residents', 'occupied_beds', 'available_beds', 'total_beds'];
      const missingFields = requiredFields.filter(field => !(field in response.data));
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }
      
      // All-zeros is a valid state for a brand-new/empty shelter — not an error.
      const numericValues = Object.values(response.data).filter(v => typeof v === 'number');
      const allZeros = numericValues.length > 0 && numericValues.every(v => v === 0);
      if (allZeros) {
        console.log('ℹ️ Dashboard stats are all zero (new/empty shelter)');
      } else {
        console.log('✅ Dashboard has valid data:', numericValues.filter(v => v > 0));
      }
      
      setStats(response.data);
    } catch (error) {
      console.error(`❌ Error fetching dashboard stats (attempt ${retryCount + 1}):`, error);
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        console.error('🔐 Authentication failed - user needs to log in again');
        toast.error('Session expired. Please log in again.');
        
        // Clear invalid auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        
        setStats({ _error: true, _auth_required: true });
        
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        return;
      }
      
      if (retryCount < maxRetries) {
        console.log(`🔄 Retrying in ${(retryCount + 1) * 2} seconds...`);
        setTimeout(() => fetchDashboardStats(retryCount + 1), (retryCount + 1) * 2000);
        return;
      }
      
      // Final fallback - show error state
      console.error('❌ All retry attempts failed');
      toast.error(`Failed to load dashboard statistics after ${maxRetries + 1} attempts`);
      
      // Set zero stats but mark as error state
      const errorStats = {
        total_residents: 0,
        occupied_beds: 0,
        available_beds: 0,
        total_beds: 0,
        long_stay_residents: 0,
        new_residents_today: 0,
        staff_on_duty: 0,
        meals_served_today: 0,
        pending_chores: 0,
        completed_chores_today: 0,
        _error: true // Flag to indicate this is error state
      };
      setStats(errorStats);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentShiftLogs = async () => {
    try {
      const response = await axios.get(`${API}/shift-logs/all?days=3`);
      setRecentShiftLogs(response.data.shift_logs.slice(0, 3)); // Get last 3 shift logs from past 3 days
    } catch (error) {
      console.error('Error fetching recent shift logs:', error);
      // Don't show error toast for this as it's supplementary data
    }
  };

  const quickActions = [
    {
      title: 'Add New Resident',
      description: 'Register a new shelter resident',
      icon: Plus,
      href: '/residents',
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      title: 'Room Assignment',
      description: 'Assign residents to rooms',
      icon: Building,
      href: '/rooms',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      title: 'Add Case Note',
      description: 'Document resident interactions',
      icon: FileText,
      href: '/case-notes',
      color: 'bg-purple-500 hover:bg-purple-600'
    }
  ];

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Welcome to Shelter Care Connect</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="card-shadow">
              <CardContent className="p-6">
                <div className="skeleton h-16 w-full rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <Activity className="h-8 w-8 text-indigo-600" />
            <span>Dashboard</span>
          </h1>
          <p className="text-gray-600 mt-1">
            {formatDate(currentTime)} • {formatTime(currentTime)}
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex space-x-2">
          <Button
            onClick={() => {
              console.log('🔄 Manual refresh triggered');
              setLoading(true);
              fetchDashboardStats(0);
              toast.success('Dashboard data refreshed successfully');
            }}
            variant="outline"
            className="flex items-center space-x-2"
            disabled={loading}
          >
            <Activity className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>{loading ? 'Refreshing...' : 'Refresh Data'}</span>
          </Button>
          
          <Button
            onClick={() => {
              // Clear cache and reload
              if ('caches' in window) {
                caches.keys().then(names => {
                  names.forEach(name => caches.delete(name));
                });
              }
              window.location.reload();
            }}
            variant="outline"
            className="flex items-center space-x-2 border-orange-200 text-orange-700 hover:bg-orange-50"
          >
            <AlertTriangle className="h-4 w-4" />
            <span>Clear Cache</span>
          </Button>
        </div>
      </div>

      {/* Full Occupancy Info (show when all beds are occupied) */}
      {stats && stats.available_beds === 0 && stats.occupied_beds > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <Building className="h-5 w-5 text-orange-600" />
            <h3 className="font-medium text-orange-900">Shelter at Full Capacity</h3>
          </div>
          <p className="text-sm text-orange-700 mt-2">
            All {stats.occupied_beds} beds are currently occupied (100% occupancy). 
            {stats.total_residents - 17} residents are in the waiting list or unassigned.
          </p>
          <div className="mt-3">
            <Button 
              onClick={() => {
                console.log('🔄 Refreshing occupancy data');
                setLoading(true);
                fetchDashboardStats(0);
              }}
              size="sm" 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Check for Available Beds'}
            </Button>
          </div>
        </div>
      )}

      {/* Authentication required */}
      {stats && stats._auth_required && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-testid="dashboard-auth-panel">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h3 className="font-medium text-amber-900">Session expired</h3>
          </div>
          <p className="text-sm text-amber-700 mt-2">Please sign in again to view your dashboard.</p>
          <Button onClick={() => (window.location.href = '/login')} size="sm" className="bg-amber-600 hover:bg-amber-700 mt-3" data-testid="dashboard-relogin-btn">
            Go to Login
          </Button>
        </div>
      )}

      {/* Genuine data-load failure */}
      {stats && stats._error && !stats._auth_required && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="dashboard-error-panel">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="font-medium text-red-900">Couldn't load dashboard data</h3>
          </div>
          <p className="text-sm text-red-700 mt-2">There was a problem reaching the server. Please try again.</p>
          <div className="mt-3 space-x-2">
            <Button onClick={() => fetchDashboardStats(0)} size="sm" className="bg-red-600 hover:bg-red-700" data-testid="dashboard-retry-btn">
              <RefreshCw className="h-4 w-4 mr-2" />Retry
            </Button>
            <Button onClick={() => window.location.reload()} size="sm" variant="outline">Reload Page</Button>
          </div>
        </div>
      )}

      {/* Friendly empty state for a new/empty shelter (no error) */}
      {stats && !stats._error && !stats._auth_required &&
        stats.total_residents === 0 && stats.total_beds === 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mb-6" data-testid="dashboard-empty-state">
          <div className="flex items-center space-x-2 mb-2">
            <Home className="h-5 w-5 text-indigo-600" />
            <h3 className="font-semibold text-indigo-900">Welcome — let's set up your shelter</h3>
          </div>
          <p className="text-sm text-indigo-700 mb-4">
            Your dashboard is empty because you haven't added any rooms or residents yet. Get started in two quick steps:
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/rooms">
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="empty-add-rooms-btn">
                <Building className="h-4 w-4 mr-2" />Add Rooms & Beds
              </Button>
            </Link>
            <Link to="/residents">
              <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700" data-testid="empty-add-resident-btn">
                <Plus className="h-4 w-4 mr-2" />Add Your First Resident
              </Button>
            </Link>
            <Button size="sm" variant="ghost" onClick={handleLoadSample} disabled={sampleLoading} className="text-indigo-600" data-testid="load-sample-btn">
              {sampleLoading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Loading...</> : <><Activity className="h-4 w-4 mr-2" />Load Sample Data</>}
            </Button>
          </div>
          <p className="text-xs text-indigo-500 mt-3">New here? "Load Sample Data" fills the app with example residents, rooms and chores so you can explore — remove it anytime.</p>
        </div>
      )}

      {/* Sample data banner */}
      {hasSampleData && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-6" data-testid="sample-data-banner">
          <span className="text-sm text-amber-800">You're viewing <strong>sample data</strong>. Clear it before adding real residents.</span>
          <Button size="sm" variant="outline" onClick={handleClearSample} disabled={sampleLoading} className="border-amber-300 text-amber-800" data-testid="clear-sample-btn">
            {sampleLoading ? 'Working...' : 'Clear Sample Data'}
          </Button>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="card-shadow hover:card-shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Current Residents</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_residents || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <Link to="/residents">
                <Button variant="ghost" size="sm" className="w-full text-blue-600 hover:text-blue-700">
                  View All Residents
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow hover:card-shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Occupied Beds</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.occupied_beds || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Building className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="mt-4">
              <Link to="/rooms">
                <Button variant="ghost" size="sm" className="w-full text-orange-600 hover:text-orange-700">
                  Manage Rooms
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow hover:card-shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available Beds</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.available_beds || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Home className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <Badge variant="outline" className="w-full justify-center text-green-700 border-green-200">
                Ready for Assignment
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow hover:card-shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Maintenance Rooms</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.maintenance_rooms || 0}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Settings className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <div className="mt-4">
              <Link to="/maintenance-requests">
                <Button variant="ghost" size="sm" className="w-full text-orange-600 hover:text-orange-700">
                  View Requests
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow hover:card-shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Long Stay Residents</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.long_stay_residents || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Clock className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="mt-4">
              <Badge variant="outline" className="w-full justify-center text-red-700 border-red-200">
                30+ Days
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {quickActions.map((action, index) => {
          const Icon = action.icon;
          return (
            <Card key={index} className="card-shadow hover:card-shadow-lg transition-all hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 ${action.color} rounded-full transition-colors`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{action.title}</h3>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Link to={action.href}>
                    <Button className="w-full" variant="outline">
                      Get Started
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Today's Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              <span>Today's Activity</span>
            </CardTitle>
            <CardDescription>Summary of today's key metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-900">New Admissions</span>
                </div>
                <Badge className="bg-green-100 text-green-800">
                  {stats?.new_residents_today || 0}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Building className="h-5 w-5 text-blue-600" />
                  <div>
                    <span className="font-medium text-blue-900">Occupancy Rate</span>
                    <p className="text-xs text-blue-600 mt-0.5">
                      {stats?.occupied_beds || 0} occupied, {stats?.available_beds || 0} available
                    </p>
                  </div>
                </div>
                <Badge className="bg-blue-100 text-blue-800">
                  {stats?.occupied_beds >= 0 && stats?.total_beds > 0
                    ? Math.round((stats.occupied_beds / stats.total_beds) * 100)
                    : 0}%
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span>Attention Needed</span>
            </CardTitle>
            <CardDescription>Items requiring staff attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.long_stay_residents > 0 && (
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-amber-600" />
                    <span className="font-medium text-amber-900">Long-term Residents</span>
                  </div>
                  <Badge className="bg-amber-100 text-amber-800">
                    {stats.long_stay_residents} residents
                  </Badge>
                </div>
              )}
              
              {(stats?.available_beds === 0) && (
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Home className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-red-900">No Available Beds</span>
                  </div>
                  <Badge className="bg-red-100 text-red-800">
                    Full Capacity
                  </Badge>
                </div>
              )}
              
              {stats?.long_stay_residents === 0 && stats?.available_beds > 0 && (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No urgent items requiring attention</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              <span>Recent Shift Notes</span>
            </CardTitle>
            <CardDescription>Latest handoff notes from staff</CardDescription>
          </CardHeader>
          <CardContent>
            {recentShiftLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No recent shift notes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentShiftLogs.map((log) => (
                  <div key={log.id} className="p-3 bg-indigo-50 rounded-lg border-l-4 border-indigo-400">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-indigo-900 text-sm">{log.staff_name}</span>
                      <span className="text-xs text-indigo-600">
                        {new Date(log.created_at).toLocaleTimeString('en-US', {
                          timeZone: 'America/Chicago',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </span>
                    </div>
                    {log.shift_notes && (
                      <p className="text-sm text-gray-700 line-clamp-2">{log.shift_notes}</p>
                    )}
                    {log.issues_to_report && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                        <p className="text-xs text-red-800 font-medium">Issue: {log.issues_to_report}</p>
                      </div>
                    )}
                  </div>
                ))}
                
                <div className="text-center pt-2">
                  <Link to="/shift-logs">
                    <Button variant="outline" size="sm" className="text-indigo-600 hover:text-indigo-700">
                      View All
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;